import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {Article, BreadcrumbList, CollectionPage, JobPosting, LocalBusiness, WithContext} from 'schema-dts';
import type {FolderInterface, ListViewDataArticle, ListViewDataJobPosting, Website} from 'types/payload-types';

/**
 * Generates the base URL for a given tenant based on its domain information.
 * This helper function checks whether the provided `tenant` is defined and fully populated.
 * If so, it constructs a secure `https://` base URL using the tenant's domain. If the tenant is
 * missing or incomplete, it returns an empty string as a fallback.
 * This is commonly used for building canonical URLs, asset paths, or structured data links
 * in multi-tenant applications where each tenant has a unique domain.
 *
 * @param tenant The tenant object or string, possibly `null` or `undefined`.
 * @returns A valid base URL (e.g. `https://example.com`) or an empty string.
 *
 * @example
 * ```ts
 * const url = getBaseUrl(tenant); // => "https://lachenschmid.com"
 * ```
 */
const getBaseUrl = (tenant: Website | string | null | undefined) => (isDefinedAndPopulated(tenant) ? `https://${tenant.domain}` : '');

/**
 * Recursively transforms a nested `FolderInterface` structure into a flat array of breadcrumb objects.
 * This utility function is commonly used to generate structured breadcrumb trails for use in navigation,
 * SEO metadata, or JSON-LD schema generation. It handles deeply nested folder hierarchies by recursively
 * walking up the `folder.folder` chain and accumulating breadcrumb entries in the correct order.
 * If the input is `undefined`, `null`, or a string, it returns an empty array. Otherwise, it builds a breadcrumb
 * list of objects with `name` and `slug` properties, from the top-level folder down to the current one.
 *
 * @param folder A folder object conforming to `FolderInterface`, or `undefined`, `null`, or a string.
 * @returns An ordered array of breadcrumb items with `name` and `slug`, representing the folder hierarchy.
 *
 * @example
 * ```ts
 * const breadcrumbs = recursiveBreadcrumbs({
 *   name: "Subfolder",
 *   slug: "subfolder",
 *   folder: {
 *     name: "Parent",
 *     slug: "parent",
 *     folder: null
 *   }
 * });
 * // → [{ name: "Parent", slug: "parent" }, { name: "Subfolder", slug: "subfolder" }]
 * ```
 */
const recursiveBreadcrumbs = (folder?: FolderInterface | string | null): {name: string; slug: string}[] => {
    if (!folder || typeof folder === 'string') return [];

    return folder.folder ? [
        ...recursiveBreadcrumbs(folder.folder),
        {
            name: folder.name,
            slug: folder.slug
        }
    ] : [{
        name: folder.name,
        slug: folder.slug
    }];
};

/**
 * Builds a JSON-LD `BreadcrumbList` object compliant with schema.org for SEO purposes.
 * This function constructs a structured breadcrumb trail based on the provided tenant domain, SEO data, and optional folder hierarchy.
 * It starts with the homepage, appends each folder segment recursively, and optionally includes the final page based on `seoData.slug`.
 * This output is used to enhance search engine visibility and improve rich results like Google breadcrumbs.
 * If the tenant is fully populated, its domain is used as the base URL. Otherwise, relative URLs may be incomplete.
 * Folder nesting is flattened using `recursiveFolderToBreadcrumbs`, preserving the hierarchy.
 *
 * @param tenant  The current website object or string used to determine the base domain. Can be `null`.
 * @param seoData SEO metadata including the page `name` and optional `slug`, typically from CMS page configs.
 * @param folder  An optional nested folder structure, used to build hierarchical breadcrumbs.
 * @returns A `WithContext<BreadcrumbList>` object representing the full breadcrumb schema.
 *
 * @example
 * ```ts
 * const jsonLd = buildBreadcrumbsJsonLd(tenant, { name: 'Kontakt', slug: '/kontakt' }, folder);
 * ```
 */
export const buildBreadcrumbsJsonLd = (
    tenant: Website | string | null | undefined,
    seoData?: PageData['pageMetaData']['seo'],
    folder?: PageData['pageMetaData']['structuredData']['breadcrumbs']
): WithContext<BreadcrumbList> => {
    const baseUrl = getBaseUrl(tenant);
    const crumbs = recursiveBreadcrumbs(folder);

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                item: baseUrl,
                name: 'Startseite',
                position: 1
            },
            ...(crumbs.map((crumb, index) => ({
                '@type': 'ListItem' as const,
                item: `${baseUrl}${crumb.slug}`,
                name: crumb.name,
                // eslint-disable-next-line @nfq/no-magic-numbers
                position: index + 2
            }))),
            ...(seoData?.slug && seoData.slug !== '/' ? [
                {
                    '@type': 'ListItem' as const,
                    item: `${baseUrl}${seoData.slug}`,
                    name: seoData.name,
                    // eslint-disable-next-line @nfq/no-magic-numbers
                    position: crumbs.length + 2
                }
            ] : [])
        ]
    };
};

/**
 * Builds a JSON-LD structured data object representing a collection of articles or job postings in a list view.
 * This function generates a `CollectionPage` schema according to [schema.org](https://schema.org/CollectionPage),
 * enriched with an `ItemList` as the main entity. It is intended to improve SEO and enable rich results for
 * listings like blog overviews, job boards, or similar structured collections.
 * It uses the provided tenant to resolve a base URL, and then maps over the `documentsAndFolders` list to extract
 * relevant metadata such as item type (`Article` or `JobPosting`), headline, and canonical URL for each item.
 * If no specific type is available, it defaults to `"Article"`.
 * This JSON-LD object can be injected into the page head as a `<script type="application/ld+json">` block.
 *
 * @param tenant   The tenant object or string that provides domain context for URL construction.
 * @param listName A human-readable name for the list or collection (e.g., "Stellenangebote").
 * @param list     An optional array of content entries with enriched list view metadata.
 * @returns A JSON-LD object compliant with `CollectionPage` and `ItemList` schema standards, or `null` if not applicable.
 *
 * @example
 * ```ts
 * const jsonLd = buildListJsonLd(tenant, 'Jobs bei Lachenschmid', folder.documentsAndFolders.docs);
 * ```
 */
export const buildListJsonLd = (
    tenant: Website | string | null | undefined,
    listName: string,
    list?: PageData['pageMetaData']['structuredData']['list'] | null
): WithContext<CollectionPage> => {
    const baseUrl = getBaseUrl(tenant);

    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: list?.map(listItem => ({
                '@type': (listItem.listView.type as 'Article' | 'JobPosting' | undefined) ?? 'Article',
                headline: listItem.listView.headline,
                url: `${baseUrl}${listItem.slug}`
            }))
        },
        name: listName
    };
};

/**
 * Builds a JSON-LD structured data object representing a single article for SEO purposes.
 * This function generates an `Article` schema compliant with [schema.org](https://schema.org/Article),
 * suitable for use in search engine optimization and rich search results. It includes relevant metadata such as
 * headline, description, publisher, and image.
 * The method requires a `tenant` object with domain and organizational metadata as well as a `listView` object
 * containing the article's summary data. It uses this information to construct a rich semantic object for
 * structured data embedding.
 * The output should be injected into the HTML document as a `<script type="application/ld+json">` block.
 *
 * @param tenant   The tenant that owns the article. Must include domain, logo, and `organisationMetaData`.
 * @param listData The `listView` data object of the article, typically defined in the CMS.
 * @returns A JSON-LD object representing the article in schema.org format, or `null` if the tenant is incomplete.
 *
 * @example
 * ```ts
 * const jsonLd = buildArticleJsonLd(tenant, blogPage.listView);
 * ```
 */
export const buildArticleJsonLd = (
    tenant: Website | string | null | undefined,
    listData: (ListViewDataArticle & {createdAt?: string; updatedAt?: string}) | null | undefined
): WithContext<Article> | null => {
    if (
        !isDefinedAndPopulated(tenant)
        || !isDefined(tenant.organisationMetaData)
        || !isDefinedAndPopulated(tenant.logo)
    ) return null;

    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        author: {
            '@type': 'Person',
            name: isDefinedAndPopulated(listData?.author) ? listData.author.name : tenant.organisationMetaData.orgName
        },
        dateModified: listData?.updatedAt,
        datePublished: listData?.createdAt,
        description: listData?.listDescription,
        headline: listData?.headline,
        image: `${getBaseUrl(tenant)}${isDefinedAndPopulated(listData?.listImage) ? listData.listImage.sizes?.sm?.url ?? listData.listImage.url : ''}`,
        publisher: {
            '@type': 'Organization',
            logo: {
                '@type': 'ImageObject',
                url: `${getBaseUrl(tenant)}${tenant.logo.url}`
            },
            name: tenant.organisationMetaData.legalName ?? ''
        }
    };
};

/**
 * Builds a JSON-LD structured data object for a single job posting to improve SEO and support rich search features.
 * This function generates a `JobPosting` schema following the [schema.org](https://schema.org/JobPosting) standard,
 * embedding essential metadata such as job title, description, employment type, valid date range, and organizational info.
 * It enables search engines to understand and present job listings in a rich, enhanced way (e.g., in Google for Jobs).
 * The function uses both the tenant's metadata and the list view content of the job posting. It requires the tenant to
 * be populated with a valid domain, logo, and organizational address information to construct the full schema.
 *
 * @param tenant   The tenant context containing domain, logo, and organization metadata for the job poster.
 * @param listData The `listView` object of the job, including title, description, and dates.
 * @returns A JSON-LD object in `JobPosting` schema format, or `null` if required tenant data is missing.
 *
 * @example
 * ```ts
 * const jsonLd = buildJobJsonLd(tenant, job.listView);
 * ```
 */
export const buildJobJsonLd = (
    tenant: Website | string | null | undefined,
    listData: (ListViewDataJobPosting & {createdAt?: string; updatedAt?: string}) | null | undefined
): WithContext<JobPosting> | null => {
    if (
        !isDefinedAndPopulated(tenant)
        || !isDefined(tenant.organisationMetaData)
        || !isDefinedAndPopulated(tenant.logo)
    ) return null;
    const org = tenant.organisationMetaData;

    return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        datePosted: listData?.createdAt,
        description: listData?.listDescription,
        employmentType: listData?.employmentType,
        hiringOrganization: {
            '@type': 'Organization',
            logo: `${getBaseUrl(tenant)}${tenant.logo.url}`,
            name: tenant.organisationMetaData.legalName ?? '',
            sameAs: getBaseUrl(tenant)
        },
        jobLocation: {
            '@type': 'Place',
            address: {
                '@type': 'PostalAddress',
                addressCountry: org.address?.addressCountry ?? '',
                addressLocality: org.address?.addressLocality ?? '',
                postalCode: org.address?.postalCode ?? '',
                streetAddress: org.address?.streetAddress ?? ''
            }
        },
        title: listData?.headline,
        validThrough: listData?.validThrough
    };
};

/**
 * Builds a JSON-LD structured data object representing an organization, conforming to Schema.org's `LocalBusiness` type.
 * This function is intended for SEO purposes and is typically injected into the HTML `<head>` of a page to improve discoverability
 * and enhance search result appearance (e.g., showing business info in rich results).
 * It uses the tenant’s `organisationMetaData`, `domain`, and `logo` to populate details such as business name, address,
 * contact info, and social profiles.
 *
 * If any of the required tenant properties (`organisationMetaData` and `logo`) are missing or undefined,
 * the function returns `null`, indicating that structured data should not be rendered.
 *
 * @param tenant The current tenant (website) object or `null`. Must include `organisationMetaData` and a populated `logo` to generate output.
 * @returns A `WithContext<LocalBusiness>` JSON-LD object or `null` if required data is missing.
 *
 * @example
 * ```tsx
 * const jsonLd = buildOrganisationJsonLd(currentTenant);
 * if (jsonLd) {
 *   <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
 * }
 * ```
 */
export const buildOrganisationJsonLd = (
    tenant: Website | string | null | undefined
): WithContext<LocalBusiness> | null => {
    if (
        !isDefinedAndPopulated(tenant)
        || !isDefined(tenant.organisationMetaData)
        || !isDefinedAndPopulated(tenant.logo)
    ) return null;
    const org = tenant.organisationMetaData;

    return {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        address: {
            '@type': 'PostalAddress',
            addressCountry: org.address?.addressCountry ?? '',
            addressLocality: org.address?.addressLocality ?? '',
            postalCode: org.address?.postalCode ?? '',
            streetAddress: org.address?.streetAddress ?? ''
        },
        email: org.email ?? '',
        founder: org.founder ?? '',
        foundingDate: org.foundingDate ?? '',
        legalName: org.legalName ?? '',
        logo: `${getBaseUrl(tenant)}${tenant.logo.url}`,
        name: org.orgName,
        sameAs: org.sameAs?.map(social => social.url ?? '') ?? [],
        telephone: org.telephone ?? '',
        url: getBaseUrl(tenant)
    };
};